import React, { useMemo } from 'react';
import {
  Box, Heading, HStack, ScrollView, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import type { MeetingJoinSettings } from '@/common/components/meeting/types';
import { useOralExamCohortState } from '@/common/hooks/oralExam/useOralExamCohortState';
import type { OralExamBookingRunSummary } from '@/common/api/models/oralExamBookingRunSummary';

import { IdConsentReview, type IdConsentReviewItem, resolveBandState } from './IdConsentReview';
import {
  ID_REVIEW_TABLE_WIDTH,
  IdReviewTableHeader,
  isPhotoBand,
} from './IdConsentReview/IdReviewBand';
import { LecturerPresenceSetup } from './LecturerPresenceSetup';
import { PREFLIGHT_COLUMN_TITLE_HEIGHT, PreflightColumnLabel } from './PreflightColumnLabel';
import { PreflightSupervisorsColumn } from './PreflightSupervisorsColumn';
import { PREFLIGHT_SUPERVISORS_COLUMN_WIDTH } from './PreflightSupervisorsColumnLayout';
import {
  isStartGateItemReady,
  StartGate,
  type StartGateReadinessItem,
  useStartGateFact,
} from './StartGate';
import { StartGateHeaderRow, StartGateReadyCount } from './StartGateStatusBar';
import { isStartGateReady, resolveEffectiveReadiness } from './StudentLiveTile';

const ROW_NOWRAP = { flexWrap: 'nowrap' as const };

export type LecturerPreflightPresenceSetupProps = {
  onConfirm: (args: { imageBase64: string; saveAsProfile: boolean }) => void;
  savedPhotoUrl?: string | null;
  savedPhotoResolving?: boolean;
  savedPhotoLoadFailed?: boolean;
  retrySavedPhotoLoad?: () => void;
};

export type LecturerPreflightStageProps = {
  slotId: string;
  slotStartIso?: string | null;
  slotEndIso?: string | null;
  joinSettings?: MeetingJoinSettings;
  joinCreds?: { token: string; livekitUrl: string; roomName?: string } | null;
  /** Ends cohort-token quiet remint wait once the preflight room connects. */
  onRoomTokenConnected?: () => void;
  /**
   * Offline avatar capture, before the lecturer joins the pre-flight room.
   * The supervisors column is not mounted on this step.
   */
  presenceSetup?: LecturerPreflightPresenceSetupProps | null;
};

/** Whitespace between the student table and the supervisors column; no rule. */
export const PREFLIGHT_DESK_GUTTER = 64;
const PAGE_PADDING_X = 24;
const PAGE_PADDING_TOP = 32;
const SCROLL_CENTER = { flexGrow: 1, justifyContent: 'center' as const };

/** Fixed desktop content width: the page scrolls sideways below it, never wraps. */
export function preflightContentWidth(showSupervisorsColumn: boolean): number {
  return ID_REVIEW_TABLE_WIDTH
    + (showSupervisorsColumn ? PREFLIGHT_DESK_GUTTER + PREFLIGHT_SUPERVISORS_COLUMN_WIDTH : 0);
}

function mapReviewItems(bookingRuns: OralExamBookingRunSummary[]): IdConsentReviewItem[] {
  return bookingRuns.map((run) => ({
    bookingId: run.bookingId,
    courseId: run.courseId,
    moduleId: run.moduleId,
    taskId: run.taskId,
    studentName: run.studentName ?? run.bookingId,
    readiness: resolveEffectiveReadiness(run),
    consentRecordedAt: run.runState.consentRecord?.grantedAt ?? null,
    idPhotoStatus: run.idVerification.status,
    idPhotoBlobPath: run.idPhotoBlobPath ?? null,
    idSelfiePhotoBlobPath: run.idSelfiePhotoBlobPath ?? null,
    etag: run.runState.etag,
  }));
}

function mapReadiness(bookingRuns: OralExamBookingRunSummary[]): StartGateReadinessItem[] {
  return bookingRuns.map((run) => {
    const readiness = resolveEffectiveReadiness(run);
    return {
      bookingId: run.bookingId,
      studentName: run.studentName ?? run.bookingId,
      isReady: isStartGateReady(readiness),
      readiness,
    };
  });
}

/**
 * Lecturer pre-flight review: accept each student's identity + consent, then open
 * the start gate (oral-exam-1n-lecturer-ux §3, mockup lecturer-preflight-review).
 * One capacity-driven screen — N rows for a cohort, one card for a single student.
 * "Set up group briefing" is offline: a local camera, no room, no supervisors
 * column. After the lecturer confirms the photo, pre-flight is the meeting
 * room and the column is on the right. Capacity-1 lecturers still capture in
 * `LecturerPresenceSetupGate`.
 */
export function LecturerPreflightStage({
  slotId,
  slotStartIso,
  slotEndIso,
  joinSettings,
  joinCreds,
  onRoomTokenConnected,
  presenceSetup = null,
}: LecturerPreflightStageProps) {
  const { t } = useTranslation();
  const {
    cohort, bookingRuns, capacity,
  } = useOralExamCohortState(slotId, { polling: true });

  const reviewItems = useMemo(
    () => mapReviewItems(bookingRuns),
    [bookingRuns],
  );

  const readiness = useMemo(
    () => mapReadiness(bookingRuns),
    [bookingRuns],
  );

  const gateFact = useStartGateFact(slotStartIso, slotEndIso);

  // Slot capacity (authoritative) decides 1:1 vs cohort — NOT the booked count.
  const studentCount = bookingRuns.length;
  const supervisorNoShowAt = cohort?.supervisorNoShowAt ?? null;
  const technicalCancelledAt = cohort?.technicalCancelledAt ?? null;
  const supervisorNoShowCancelled = Boolean(supervisorNoShowAt);
  const isSingle = capacity <= 1;
  const showSupervisorsColumn = !isSingle && joinSettings != null;
  const capacityLabel = isSingle
    ? t('oralExam.lecturer.preflightReview.capacitySingle')
    : t('oralExam.lecturer.preflightReview.capacityCohort', { count: capacity });
  if (presenceSetup) {
    return (
      <Box flex={1} testID="lecturer-preflight-stage">
        <LecturerPresenceSetup
          onConfirm={presenceSetup.onConfirm}
          savedPhotoUrl={presenceSetup.savedPhotoUrl}
          savedPhotoResolving={presenceSetup.savedPhotoResolving}
          savedPhotoLoadFailed={presenceSetup.savedPhotoLoadFailed}
          onRetrySavedPhotoLoad={presenceSetup.retrySavedPhotoLoad}
        />
      </Box>
    );
  }

  const showStartGate = studentCount > 0 || supervisorNoShowCancelled;
  const sessionEnded = supervisorNoShowCancelled || Boolean(technicalCancelledAt);
  const readyCount = readiness.filter(isStartGateItemReady).length;
  const anyPhotoRow = reviewItems.some((item) => isPhotoBand(resolveBandState(item, t)));

  const titleBlock = (
    <VStack space="sm" testID="preflight-title-row">
      <Heading size="xl">{t('oralExam.lecturer.preflightReview.title')}</Heading>
      <HStack style={ROW_NOWRAP}>
        <Text size="sm" color="$textSubtle" testID="preflight-capacity-label">
          {capacityLabel}
        </Text>
        {gateFact ? (
          <>
            <Text size="sm" color="$textSubtle">{' · '}</Text>
            <Text size="sm" color="$textSubtle" testID={gateFact.testID}>
              {gateFact.text}
            </Text>
          </>
        ) : null}
      </HStack>
    </VStack>
  );

  return (
    <Box flex={1} position="relative">
      <ScrollView flex={1} testID="lecturer-preflight-stage">
        <ScrollView horizontal contentContainerStyle={SCROLL_CENTER}>
          <VStack
            width={preflightContentWidth(showSupervisorsColumn) + 2 * PAGE_PADDING_X}
            px={PAGE_PADDING_X}
            pt={PAGE_PADDING_TOP}
            pb={PAGE_PADDING_TOP}
          >
            {showStartGate ? (
              <StartGate
                slotId={slotId}
                capacity={capacity}
                readiness={readiness}
                slotStartIso={slotStartIso}
                slotEndIso={slotEndIso}
                supervisorNoShowAt={supervisorNoShowAt}
                technicalCancelledAt={technicalCancelledAt}
                etag={cohort?.etag}
                renderFact={false}
                leading={titleBlock}
              />
            ) : (
              <StartGateHeaderRow leading={titleBlock} />
            )}

            <HStack testID="preflight-desk" mt="$8" alignItems="flex-start" style={ROW_NOWRAP}>
              <VStack width={ID_REVIEW_TABLE_WIDTH} flexShrink={0} space="lg">
                {studentCount > 0 ? (
                  <>
                    <IdReviewTableHeader
                      showPhotoHeaders={anyPhotoRow}
                      trailing={sessionEnded ? null : (
                        <StartGateReadyCount
                          readyCount={readyCount}
                          total={readiness.length}
                          variant="columnHeader"
                        />
                      )}
                    />
                    <IdConsentReview
                      slotId={slotId}
                      reviewItems={reviewItems}
                      slotStartIso={slotStartIso}
                      slotEndIso={slotEndIso}
                    />
                  </>
                ) : (
                  <>
                    <HStack h={PREFLIGHT_COLUMN_TITLE_HEIGHT} alignItems="center">
                      <PreflightColumnLabel testID="preflight-students-column-title">
                        {t('oralExam.lecturer.preflightReview.studentsColumn')}
                      </PreflightColumnLabel>
                    </HStack>
                    {!supervisorNoShowCancelled ? (
                      <Box
                        bg="$bgSubtle"
                        p="$3"
                        borderRadius="$md"
                        testID="preflight-no-students-empty-state"
                      >
                        <Text size="sm" color="$textSubtle">
                          {t('oralExam.lecturer.preflightReview.noStudents')}
                        </Text>
                      </Box>
                    ) : null}
                  </>
                )}
              </VStack>
              {showSupervisorsColumn ? (
                <Box
                  testID="preflight-supervisors-desk-slot"
                  width={PREFLIGHT_SUPERVISORS_COLUMN_WIDTH}
                  ml={PREFLIGHT_DESK_GUTTER}
                  flexShrink={0}
                >
                  <PreflightSupervisorsColumn
                    slotId={slotId}
                    joinSettings={joinSettings}
                    joinCreds={joinCreds ?? null}
                    onRoomTokenConnected={onRoomTokenConnected}
                  />
                </Box>
              ) : null}
            </HStack>
          </VStack>
        </ScrollView>
      </ScrollView>
    </Box>
  );
}
